import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { select, Store } from '@ngxs/store';
import { ChangeCurrentSelectedDepartment } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { isDepartmentPaused } from 'src/app/shared/user-view/functions/is-department-paused';
import { IDepartmentInfo } from 'src/app/shared/user-view/models/tenant-user-access.model';
import { UnsavedDataGuard } from 'src/app/shared/guards/unsaved-data.guard';

@Component({
  selector: 'app-department-dropdown',
  templateUrl: './department-dropdown.component.html',
  styleUrls: ['./department-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DepartmentDropdownComponent {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  allAvailableDepartmentsFromStore = select(TenantUserAccessState.getAllAvailableDepartments);
  selectedDepartment = select(TenantUserAccessState.currentDepartment);
  departmentListSorted = computed(() =>
    [...(this.allAvailableDepartmentsFromStore() ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  );

  searchFilter = '';
  filteredDepartments: IDepartmentInfo[] = [];

  allDepsIconPath = '/assets/icons/all-departments.svg';
  departmentPlaceHolderIconPath = '/assets/icons/department-placeholder.svg';

  readonly allDepsToken = ALL_DEPS_TOKEN;
  readonly isDepartmentPaused = isDepartmentPaused;

  private allAvailableDepartments: IDepartmentInfo[] = [];
  private currentSelectedDepartment: IDepartmentInfo | undefined;

  constructor(
    private store: Store,
    private unsavedDataGuard: UnsavedDataGuard,
    private cdr: ChangeDetectorRef
  ) {
    effect(() => {
      this.allAvailableDepartments = this.departmentListSorted();
      this.currentSelectedDepartment = this.selectedDepartment();
      this.applyFilter();
    });
  }

  onPanelOpenedChange(opened: boolean): void {
    if (opened) {
      this.focusSearchInput();
    } else {
      this.clearSearch();
    }
  }

  onSearch(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const value = target?.value ?? '';

    if (
      event.code === 'Space' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Space'
    ) {
      event.preventDefault();
      event.stopPropagation();
      target.value += ' ';
      return;
    }

    this.searchFilter = value;
    this.applyFilter();
  }

  private applyFilter(): void {
    this.filteredDepartments = this.computeFilteredDepartments(
      this.allAvailableDepartments,
      this.currentSelectedDepartment?.id,
      this.searchFilter
    );
    this.cdr.markForCheck();
  }

  private computeFilteredDepartments(
    list: IDepartmentInfo[],
    selectedId: string | undefined,
    filterValue: string
  ): IDepartmentInfo[] {
    if (!list?.length) {
      return [];
    }
    const filter = filterValue.trim().toLowerCase();
    if (!filter) {
      return list;
    }
    const filtered = list.filter((d) => d.name.toLowerCase().includes(filter));
    const selected = list.find((d) => d.id === selectedId);
    if (
      selected &&
      selectedId &&
      selectedId !== this.allDepsToken &&
      !filtered.some((d) => d.id === selectedId)
    ) {
      return [selected, ...filtered];
    }
    return filtered;
  }

  private focusSearchInput(): void {
    this.searchFilter = '';
    this.applyFilter();
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  private clearSearch(): void {
    this.searchFilter = '';
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    this.applyFilter();
  }

  async selectDepartment(change: MatSelectChange, matSelect: MatSelect): Promise<void> {
    const canProceed = await this.unsavedDataGuard.checkUnsavedDataInCurrentComponentAsync();
    if (!canProceed) {
      const currentDepartmentId = this.store.selectSnapshot(
        TenantUserAccessState.currentDepartment
      )?.id;
      if (currentDepartmentId !== undefined) {
        matSelect.writeValue(currentDepartmentId);
      }
      return;
    }
    this.store.dispatch(new ChangeCurrentSelectedDepartment(change.value, true));
  }
}
